// Third-party
import React, { Component } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import get from 'lodash/get';
import { SeriesOptionsType } from 'highcharts';

// Components
import { withErrorBoundary } from '../../errors';
import IncomeLegend from './IncomeLegend';
import LineChart from '../deprecated/charts/LineChart';
import { LoadingSpinner } from '../../common';

// Utils
import { FormattedMessage, formatLocalizedString, formatMessage } from '@/utils/i18n';
import { deepClone } from '@/utils/objects';
import { getCommonDataPoints } from '@/utils/comparison';
import { getSeriesBeforeAfter, getDashStyle, getLineWidth } from '@/utils/charts';
import { Interval, COMPARISON_ITEM_TYPES, TIME_RANGE_PRESET } from '@/utils/constants';
import { ShortHistoryPortfoliosDisclaimer } from '../../disclaimers/comparison';
import { getIncomeStartDate } from '@/utils/income-stream';
import { getGraphPerformanceDisclaimer } from '@/utils/disclaimers/charts';
import { ROLLING_INTERVALS } from '@/utils/portfolios';
import { getLastDayOfPreviousMonth } from '@/utils/time';
import { currencyToText } from '@/utils/currency';
import { getMaxEndDate } from '@/utils/dates';
import { getDateQuickRangeFromPreset } from '@/utils/time-range-presets';

// Types
import ComparisonItem from '@/types/comparison-item';
import Comparison from '@/types/comparison';
import IncomeChartOptions from '@/types/income-chart-options';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import Nullable from '@/types/nullable';
import Timestamp from '@/types/timestamp';
import ChartItem from '@/types/chart-item';
import { LineChartDatum, LineChartOptions } from '../LineChart';
import { commonPlotTooltipFormatter, getSharedTooltip } from '../utils/tooltip';
import { getThinDashedLineCrosshair } from '../utils/crosshair';

type Props = {
  items: ComparisonItem[];
  comparison: Comparison;
  dark?: boolean;
  compact?: boolean;
  options: IncomeChartOptions;
  onOptionsChange: (options: IncomeChartOptions) => void;
  withLegend?: boolean;
  title?: string;
  shortHistory?: boolean;
  editedComparisonItem?: ComparisonItem;
  editedScenario?: IncomeStreamScenario;
  calculateIncomeStream?: (startDate: Date, endDate: Date) => void;
  isAddingToChart?: boolean;
  readOnly?: boolean;
  showBaseCurrencies?: boolean;
} & Partial<DefaultProps>;

type DefaultProps = {
  animate: boolean;
  height: Nullable<number>;
  showDateRange: boolean;
};

type State = {
  startDate: Date;
  endDate: Date;
};

const DEFAULT_LINE_COLOR = '#2d8eff';
const NAV_FILTER = 'nav';

class IncomeChart extends Component<Props, State> {
  static defaultProps = {
    animate: true,
    height: null,
    options: {},
    onOptionsChange: () => {},
    showDateRange: true,
  };

  componentDidMount() {
    const { items, calculateIncomeStream } = this.props;
    const earliestStartDate = this.getGraphStartDate(items);

    if (items.length && calculateIncomeStream) {
      calculateIncomeStream(earliestStartDate, this.state.endDate);
    }
  }

  getGraphStartDate = (items: ComparisonItem[]) => {
    const { shortHistory } = this.props;
    const { startDate } = this.state;

    let earliestStartDate = startDate;

    // If any of the items has less data we'll set the default range to 1Y
    for (const item of items) {
      const navs = item.holdings.map(h => h.nav);

      for (const nav of navs) {
        const monthlyNav = (nav !== undefined && nav.monthly) || [];

        if (monthlyNav.length && monthlyNav[0][0] > earliestStartDate.getTime()) {
          earliestStartDate = moment(monthlyNav[0][0]).toDate();
        }
      }
    }

    return shortHistory ? startDate : earliestStartDate;
  };

  getDefaultEndDate = () => getMaxEndDate(this.props.comparison.monthEndDataOption, getLastDayOfPreviousMonth());

  readonly state: State = {
    startDate: getIncomeStartDate(this.props.comparison),
    endDate: this.getDefaultEndDate(),
  };

  getChartOptions = (): LineChartOptions => ({
    series: {} as any,
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    navigator: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    chart: {
      height: this.props.height,
      spacingLeft: 0,
      spacingRight: 0,
    },
    title: {
      text: undefined,
    },
    tooltip: getSharedTooltip(commonPlotTooltipFormatter),
    xAxis: {
      crosshair: getThinDashedLineCrosshair(),
      tickWidth: 0,
      lineWidth: 0,
    },
    yAxis: {
      title: {
        text: null,
      },
      labels: {
        x: -5,
        y: -8,
      },
      startOnTick: true,
    },
    plotOptions: {
      line: {
        marker: {
          symbol: 'circle',
          radius: 0,
          states: {
            hover: {
              lineWidth: 3,
              radius: 5,
            },
            inactive: {
              opacity: 0,
            },
          },
        },
      },
      series: {
        animation: this.props.animate,
      },
    },
  });

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { options, items, editedScenario, editedComparisonItem, shortHistory, calculateIncomeStream, comparison } = this.props;
    let { startDate, endDate } = this.state;

    if (options.startDate && prevProps.options.startDate !== options.startDate) {
      ({ startDate } = options);
    }

    if (options.endDate && prevProps.options.endDate !== options.endDate) {
      ({ endDate } = options);
    }

    if (comparison.monthEndDataOption !== prevProps.comparison.monthEndDataOption) {
      endDate = this.getDefaultEndDate();
    }

    const itemsChanged = prevProps.items.length !== items.length && !editedComparisonItem;
    const scenariosChanged =
      (prevProps.editedScenario !== undefined && editedScenario === undefined) ||
      this.getTotalNumberOfScenarios(prevProps.items) !== this.getTotalNumberOfScenarios(items);

    if (
      (startDate.getTime() !== prevState.startDate.getTime() ||
        endDate.getTime() !== prevState.endDate.getTime() ||
        itemsChanged ||
        shortHistory !== prevProps.shortHistory ||
        scenariosChanged) &&
      calculateIncomeStream
    ) {
      const earliestStartDate = this.getGraphStartDate(items);

      calculateIncomeStream(earliestStartDate, endDate);
    }
  }

  getTotalNumberOfScenarios = (items: ComparisonItem[]) =>
    items.reduce((acc, curr) => {
      if (curr.scenarios === undefined || curr.scenarios.incomeStreamDistribution === undefined) {
        return acc;
      }

      return acc + curr.scenarios.incomeStreamDistribution.length;
    }, 0);

  setOptions = <K extends keyof IncomeChartOptions>(options: Pick<IncomeChartOptions, K>) => {
    this.setState({ ...this.state, ...options }, () => {
      this.props.onOptionsChange(options);
    });
  };

  onChangeDate = (startDate: Nullable<Date>) => {
    this.setOptions({ startDate, endDate: this.getDefaultEndDate() });
  };

  getScenarioSeriesData = (item: ComparisonItem, interval: Interval): LineChartDatum[] => {
    const { showBaseCurrencies, shortHistory } = this.props;
    const { startDate } = this.state;
    const defaultScenarioGraphData = deepClone(get(item, 'scenarios.defaultIncomeStream.data.graphData') as [Timestamp, number][]);
    const otherScenarios = deepClone(get(item, 'scenarios.incomeStreamDistribution', []) as IncomeStreamScenario[]);
    const scenarioData = [defaultScenarioGraphData, ...otherScenarios.map(s => s.data && s.data.graphData)];

    const seriesData: LineChartDatum[] = [];

    for (let i = 0; i < scenarioData.length; i++) {
      const data = scenarioData[i];

      if (!data) {
        continue;
      }

      const commonProps: SeriesOptionsType = {
        itemType: item.type,
        color: item.color || DEFAULT_LINE_COLOR,
        tooltip: {
          valuePrefix: showBaseCurrencies ? `(${currencyToText(item.currency)})` : undefined,
          ...getSharedTooltip(commonPlotTooltipFormatter),
        },
        dashStyle: getDashStyle(item, undefined, false, i !== 0),
        lineWidth: getLineWidth(item, undefined, i !== 0),
        type: 'line',
      };

      const scenarioName = i === 0 ? formatLocalizedString(item.name) : formatMessage('Comparison.IncomeStream.Scenario', { key: i });

      if (shortHistory && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
        const breakingPoint = getCommonDataPoints([item], interval, startDate, 'ASC')[interval];
        const splitData = getSeriesBeforeAfter(item, data, startDate.getTime(), breakingPoint, false, scenarioName);

        seriesData.push({
          ...splitData.before,
          ...commonProps,
          dashStyle: getDashStyle(item, undefined, true, i !== 0),
          hideFromLegend: true,
        });

        seriesData.push({
          ...splitData.after,
          ...commonProps,
        });
      } else {
        seriesData.push({
          ...commonProps,
          name: scenarioName,
          data,
        });
      }
    }

    return seriesData;
  };

  formatData = (): LineChartDatum[] => {
    const { items } = this.props;
    const interval = Interval.Monthly;

    return items.reduce((acc, item) => acc.concat(this.getScenarioSeriesData(item, interval)), [] as LineChartDatum[]);
  };

  formatChartItems = () => {
    const { items, shortHistory } = this.props;
    const { startDate } = this.state;

    return items.map(item => {
      const newItem = deepClone(item) as ChartItem;

      if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && shortHistory) {
        const timestamp = moment.utc(startDate).endOf('month').valueOf();
        newItem.isShort = newItem.holdings.some(h => !!h.nav && !!h.nav[Interval.Monthly].length && h.nav[Interval.Monthly][0][0] > timestamp);
      }

      return newItem;
    });
  };

  render() {
    const { dark, items, withLegend, title, showDateRange, shortHistory, isAddingToChart, readOnly, comparison } = this.props;
    const { startDate, endDate } = this.state;
    const data = this.formatData();
    const chartItems = this.formatChartItems();
    const earliestStartDate = moment(startDate).endOf('month').toDate();
    const chartDisclaimer = getGraphPerformanceDisclaimer(chartItems, ROLLING_INTERVALS.MONTHLY, earliestStartDate, endDate, shortHistory);

    return (
      <div className={classNames('growth-chart chart income-chart', { dark })}>
        {isAddingToChart && <LoadingSpinner className="dark" />}
        {withLegend && <IncomeLegend data={data} />}

        <div className="graph-container">
          <LineChart
            dark={dark}
            data={data}
            startDate={startDate}
            endDate={endDate}
            chartOptions={this.getChartOptions()}
            title={title}
            onChangeDates={this.onChangeDate}
            yAxisOpposite
            chartDisclaimer={chartDisclaimer}
            showDateRange={showDateRange}
            showDatePickers={false}
            yearBasedValues
            emptyStateText={formatMessage('Comparison.IncomeStream.EmptyState')}
            monthEndDataOption={comparison.monthEndDataOption}
            readOnly={readOnly}
            values={getDateQuickRangeFromPreset(TIME_RANGE_PRESET.INCOME_STREAM)}
          />
        </div>

        <div className="chart-disclaimer">
          <span className="disclaimer">
            <FormattedMessage id="Comparison.IncomeStream.Disclaimer" />
          </span>

          {shortHistory && (
            <ShortHistoryPortfoliosDisclaimer
              items={items}
              disclaimerFieldFilter={NAV_FILTER}
              startDate={this.props.options?.startDate}
              endDate={this.props.options?.endDate}
            />
          )}
        </div>
      </div>
    );
  }
}

export default withErrorBoundary(IncomeChart);
